import { setReviewStatus, type SetReviewStatus, type SetReviewStatusParams } from '@/backend/entity'
import { useEntityStore } from '@/data/useEntityStore'
import type { ReviewStatus } from '@/modules/Project/Fields/types'
import { ApiError } from '@/shared/utils/error'
import { useMutation, type UseMutationOptions } from '@tanstack/vue-query'
import { toValue } from 'vue'

/**
 * Sets a field's review status in the backend and optimistically
 * updates/reverts state in the global entity store.
 */
export const useSetFieldReviewStatus = (
  options: UseMutationOptions<MutationReturn, Error, SetReviewStatusParams, MutationContext> = {},
) => {
  const entityStore = useEntityStore()

  const mutation = useMutation({
    ...options, //  spread options first because we shouldn't override the mutationFn and onMutate/onError handlers
    mutationFn: async (params) => {
      const res = await setReviewStatus(params)
      if (!res.ok) {
        throw ApiError.fromFailedResult(res)
      }

      return res.data
    },
    onMutate: async (params) => {
      return setReviewStatusInStore(params)
    },
    onError: async (error, params, context) => {
      const { oldStatus } = context || {}
      if (oldStatus) {
        setReviewStatusInStore({
          ...params,
          reviewStatus: oldStatus,
        })
      }

      /**
       * The first part of this error handler is to restore the entity to its previous state.
       * Here, we allow consumers to pass a context-dependent onError handler, which could show
       * an error message to the user.
       */
      const onError = toValue(options).onError
      if (typeof onError === 'function') {
        onError(error, params, context)
      }
    },
  })

  /**
   * Sets a field review status in the global entity store. Is used
   * to update global state when optimistically updating/reverting
   * the review status.
   *
   * @returns TanStack mutation context containing the old status
   */
  function setReviewStatusInStore(params: SetReviewStatusParams): MutationContext {
    const entityId = toValue(params.entityId)
    const propertyId = toValue(params.propertyId)
    const reviewStatus = toValue(params.reviewStatus)

    /**
     * We need to handle the full and partial entity cases separately because it's possible for either
     * to be undefined, and the full entity's fields hold full field data (+ grounding) while the partial entity's
     * fields hold partial data and no grounding.
     */
    const fullEntity = entityStore.getFullEntity(entityId)
    const fullField = fullEntity?.fields.get(propertyId)
    let oldStatus: ReviewStatus | null = null
    if (fullEntity && fullField) {
      fullEntity.fields.set(propertyId, {
        ...fullField,
        reviewStatus,
      })
      entityStore.pushEntity(fullEntity)
      oldStatus = fullField.reviewStatus
    }

    const partialEntity = entityStore.getPartialEntity(entityId)
    const partialField = partialEntity?.fields.get(propertyId)
    if (partialEntity && partialField) {
      partialEntity.fields.set(propertyId, {
        ...partialField,
        reviewStatus,
      })
      entityStore.pushPartialEntity(partialEntity)
      oldStatus = partialField.reviewStatus
    }

    return {
      oldStatus,
    }
  }

  return mutation
}

type MutationReturn = SetReviewStatus['successResponse']
type MutationContext = {
  oldStatus: ReviewStatus | null
}
